import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Sparkles } from 'lucide-react';
import { useFestivalDays, useArtists } from '../lib/usePublicData';
import Sticker from './Sticker';

const ACCENT_BG = {
    pink: 'bg-[var(--pink-soft)]',
    mint: 'bg-[var(--mint-soft)]',
    yellow: 'bg-[var(--yellow-soft)]',
    lilac: 'bg-[var(--lilac)]',
};

export const Program = () => {
    const { data: days } = useFestivalDays();
    const { data: artists } = useArtists();

    const dayWithArtists = days
        .filter((d) => (d.active === undefined ? true : d.active))
        .map((d) => {
            const list = artists
                .filter((a) => (a.festival_day_id || a.dayId) === d.id && (a.active === undefined ? true : a.active))
                .sort((a, b) => (a.sort_order ?? a.sortOrder ?? 0) - (b.sort_order ?? b.sortOrder ?? 0));
            return { ...d, artistsList: list };
        });

    return (
        <section
            id="programma"
            data-testid="program-section"
            className="relative py-16 sm:py-24 bg-cream"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="text-center mb-10 sm:mb-14">
                    <h2 data-testid="program-title" className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2.25rem, 7vw, 4.5rem)' }}>
                        PROGRAMMA
                    </h2>
                    <p className="mt-4 text-sm sm:text-base text-[#006b44]/80 max-w-2xl mx-auto">
                        Drie sfeervolle festivaldagen, telkens op een andere locatie in Ronse. Eén gemeenschappelijk doel:
                        zoveel mogelijk goesting.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
                    {dayWithArtists.map((day, idx) => {
                        const accent = day.accent_color || day.accent || 'pink';
                        return (
                            <article
                                key={day.id}
                                data-testid={`program-card-${day.id}`}
                                className={`relative ${ACCENT_BG[accent] || ACCENT_BG.pink} border-2 border-[var(--green)] rounded-[28px] p-6 sm:p-8 shadow-[0_6px_0_var(--green-dark)] transition-transform duration-300 hover:-translate-y-1`}
                            >
                                <Sticker variant="green" tilt={idx % 2 === 0 ? 'r' : 'l'} className="absolute -top-3 right-5 z-10">
                                    <Sparkles size={14} className="mr-1" /> Gratis toegang
                                </Sticker>

                                {day.description && (
                                    <Sticker variant="cream" tilt="l" className="absolute -bottom-3 left-5 z-10 !text-[10px] !py-1.5 !px-3">
                                        {day.description}
                                    </Sticker>
                                )}

                                <div className="zz-outline-title font-display text-center pt-3"
                                    style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)' }}
                                    data-testid={`program-date-${day.id}`}>
                                    {day.display_date || day.date}
                                </div>

                                <div className="mt-3 flex flex-col items-center gap-2 text-center">
                                    <span className="inline-flex items-center gap-1.5 font-display text-base text-[var(--green-dark)]">
                                        <MapPin size={16} /> {day.location}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 text-sm text-[#006b44]/80">
                                        <Clock size={14} /> {day.time_label || day.time}
                                    </span>
                                </div>

                                <ul className="mt-6 space-y-2.5">
                                    {day.artistsList.map((a) => (
                                        <li key={a.id || a.name}
                                            data-testid={`program-${day.id}-artist-${a.name}`}
                                            className={`flex items-center gap-3 ${
                                                a.headline
                                                    ? 'font-display text-lg sm:text-xl text-[var(--green-dark)]'
                                                    : 'text-base text-[#006b44]/90'
                                            }`}>
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--green)] flex-shrink-0" />
                                            <span>{a.name}</span>
                                        </li>
                                    ))}
                                    {day.artistsList.length === 0 && (
                                        <li className="text-sm text-[#006b44]/60 italic text-center">Line-up volgt binnenkort…</li>
                                    )}
                                </ul>

                                <div className="mt-7">
                                    <Link to="/#artiesten" data-testid={`program-cta-${day.id}`}
                                        className="zz-btn zz-btn--primary w-full !py-2.5 !text-xs">
                                        Bekijk artiesten <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Program;
